import { getRandomInt } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import { latinDealer } from "../Shared/Generation/latinDealer.ts";
import { MathRun, mathPlain } from "../Shared/Pdf/mathRuns.ts";
import { formatStandard } from "../Files/Prompts/decimal.ts";
import { ExponentRuleProblem, NumberPropertyOptions, ProblemData } from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";

/**
 * The rules of integer exponents, as a Number Properties question type (ADR 0136).
 *
 * Three rules, one base: a product `3² × 3⁻⁵`, a quotient `4⁵ ÷ 4⁷` and a power of
 * a power `(2³)²`. The sheet asks for the value (`= ?`); the key shows the single
 * power and then its value, `3⁻³ = 1/27`, so a teacher can tell a mistake with the
 * rule from a mistake with the arithmetic (§3).
 *
 * **The numbers (§4).** The final power must be one a student can evaluate by
 * hand: an exponent from -3 to 3 for every base, and beyond that only powers of 2
 * up to 2⁶ and 3⁴. Bases are 2 to 10. A prompt exponent is -9 to 9 and never 1;
 * 0 appears only as the outer exponent of a power of a power, `(7³)⁰`, and every
 * other zero exponent in an answer comes from cancelling.
 *
 * **Drawing.** A page is a Latin square (`latinDealer`) of rule by answer sign
 * (positive twice, zero, negative twice, so two fifths, one fifth, two fifths).
 * Then a coin decides whether a positive or zero cell's prompt has a negative
 * exponent (a negative cell's always does; drawing a pair uniformly was 73%
 * negative in ADR 0130's operations). Then a base among those with a valid pair
 * for the cell and the coin, and a pair uniformly among that base's: drawing a
 * pair across all bases at once would make most problems base 2, which has the
 * most pairs.
 *
 * Every prompt and key is `MathRun`s, because the exponents are raised
 * (`MathText`), and `display` carries the same text as `mathPlain` Unicode. The
 * symbols are the same in all three languages, so no prompt text lives in
 * `Files/Prompts`.
 */

export type ExponentRule = ExponentRuleProblem["rule"];
export type ExponentSign = "positive" | "zero" | "negative";

export const EXPONENT_RULES: ExponentRule[] = ["product", "quotient", "power"];
/** Two fifths positive, one fifth zero, two fifths negative (§4). */
export const EXPONENT_SIGNS: ExponentSign[] = ["positive", "positive", "zero", "negative", "negative"];

export const LOWEST_BASE = 2;
export const HIGHEST_BASE = 10;
/** A prompt exponent runs from -9 to 9. */
export const LOWEST_EXPONENT = -9;
export const HIGHEST_EXPONENT = 9;

/** The final power's exponent: -3 to 3 for any base, to 6 for base 2 and to 4 for base 3. */
export function finalPowerWithinCap(base: number, k: number): boolean {
    const size = Math.abs(k);
    return size <= 3 || (base === 2 && size <= 6) || (base === 3 && size <= 4);
}

function signOf(k: number): ExponentSign {
    return k > 0 ? "positive" : k < 0 ? "negative" : "zero";
}

function exponentOf(rule: ExponentRule, m: number, n: number): number {
    return rule === "product" ? m + n : rule === "quotient" ? m - n : m * n;
}

/** A value as the site writes whole numbers in this language (1,000 / 1 000), for the key. */
function whole(value: number, language: PrintedLanguage): string {
    return formatStandard(value, 0, language);
}

/**
 * An exponent-rules problem from its numbers: no draw, so a page's copy can ask
 * the generator whether an example is one it prints, and what its key says. Null
 * when a sheet would not print it (§4):
 * - the base is not 2 to 10;
 * - a prompt exponent is 1, or is outside -9 to 9;
 * - a prompt exponent is 0 anywhere but the outer exponent of a power of a power;
 * - the final power is beyond the cap (|k| of 3, or 6 for base 2, or 4 for base 3).
 */
export function exponentRuleProblem(
    rule: ExponentRule,
    base: number,
    m: number,
    n: number,
    key = 1,
    language: PrintedLanguage = "en"
): ProblemData | null {
    if (!Number.isInteger(base) || base < LOWEST_BASE || base > HIGHEST_BASE) return null;
    for (const exponent of [m, n]) {
        if (!Number.isInteger(exponent) || exponent === 1) return null;
        if (exponent < LOWEST_EXPONENT || exponent > HIGHEST_EXPONENT) return null;
    }
    if (m === 0) return null;
    if (n === 0 && rule !== "power") return null;
    const k = exponentOf(rule, m, n);
    if (!finalPowerWithinCap(base, k)) return null;

    const text = String(base);
    let prompt: MathRun[];
    if (rule === "power") {
        prompt = [{ text: `(${text}` }, { sup: String(m) }, { text: ")" }, { sup: String(n) }, { text: " = ?" }];
    } else {
        const sign = rule === "product" ? "×" : "÷";
        prompt = [{ text }, { sup: String(m) }, { text: ` ${sign} ${text}` }, { sup: String(n) }, { text: " = ?" }];
    }
    const value = base ** Math.abs(k);
    const answer: MathRun[] = [
        { text },
        { sup: String(k) },
        { text: k < 0 ? ` = 1/${whole(value, language)}` : ` = ${whole(value, language)}` },
    ];
    return {
        key,
        numbers: [],
        operator: "",
        display: [mathPlain(prompt), mathPlain(answer)],
        numberPropertyQuestion: NumberPropertyOptions.ExponentRules,
        mathPrompt: prompt,
        mathAnswer: answer,
        exponentRule: { rule, base, m, n, k },
    };
}

/** Every exponent a prompt may carry: -9 to 9 but not 1 (and 0 is only ever a power's outer one). */
const EXPONENTS = Array.from({ length: HIGHEST_EXPONENT - LOWEST_EXPONENT + 1 }, (_, i) => LOWEST_EXPONENT + i).filter(
    (exponent) => exponent !== 1
);

type Pair = [number, number];
/** Valid pairs for a cell, by base: only bases with at least one are present. */
type PairsByBase = Map<number, Pair[]>;

function pairsFor(rule: ExponentRule, sign: ExponentSign, negativePrompt: boolean): PairsByBase {
    const byBase: PairsByBase = new Map();
    for (let base = LOWEST_BASE; base <= HIGHEST_BASE; base++) {
        const pairs: Pair[] = [];
        for (const m of EXPONENTS) {
            if (m === 0) continue;
            for (const n of EXPONENTS) {
                if (n === 0 && rule !== "power") continue;
                const k = exponentOf(rule, m, n);
                if (signOf(k) !== sign || !finalPowerWithinCap(base, k)) continue;
                if ((m < 0 || n < 0) !== negativePrompt) continue;
                pairs.push([m, n]);
            }
        }
        if (pairs.length > 0) byBase.set(base, pairs);
    }
    return byBase;
}

const PAIR_TABLE = new Map<string, PairsByBase>();
function pairTable(rule: ExponentRule, sign: ExponentSign, negativePrompt: boolean): PairsByBase {
    const id = `${rule}:${sign}:${negativePrompt}`;
    let table = PAIR_TABLE.get(id);
    if (!table) {
        table = pairsFor(rule, sign, negativePrompt);
        PAIR_TABLE.set(id, table);
    }
    return table;
}

/**
 * A problem for a rule and an answer sign (§4): the coin for a negative prompt
 * exponent, then a base, then a pair. A negative answer always has a negative
 * exponent in its prompt, so it draws no coin.
 */
export function drawExponentRuleProblem(
    rule: ExponentRule,
    sign: ExponentSign,
    key: number,
    language: PrintedLanguage = "en"
): ProblemData {
    let negativePrompt = sign === "negative" ? true : getRandomInt(0, 1) === 1;
    let table = pairTable(rule, sign, negativePrompt);
    if (table.size === 0) {
        // A product that cancels to a zero exponent, a^m × a^-m, always has a negative
        // exponent, so its coin can come up empty; the draw takes the other side.
        negativePrompt = !negativePrompt;
        table = pairTable(rule, sign, negativePrompt);
    }
    const bases = Array.from(table.keys());
    const base = bases[getRandomInt(0, bases.length - 1)];
    const pairs = table.get(base)!;
    const [m, n] = pairs[getRandomInt(0, pairs.length - 1)];
    return exponentRuleProblem(rule, base, m, n, key, language)!;
}

/** One problem from any cell, for a draw that is not part of a page (spiral review). */
export function randomExponentRuleProblem(key: number, language: PrintedLanguage = "en"): ProblemData {
    const rule = EXPONENT_RULES[getRandomInt(0, EXPONENT_RULES.length - 1)];
    const sign = EXPONENT_SIGNS[getRandomInt(0, EXPONENT_SIGNS.length - 1)];
    return drawExponentRuleProblem(rule, sign, key, language);
}

/** One page's cells: rule by answer sign, a Latin square. */
const dealRuleBySign = latinDealer<ExponentRule, ExponentSign>(EXPONENT_RULES, EXPONENT_SIGNS);

interface Cell {
    rule: ExponentRule;
    sign: ExponentSign;
}

/**
 * A page-shaped set of exponent-rule problems (ADR 0136). The fingerprint is the
 * prompt within its page, so no prompt repeats on a page (and so no two
 * neighbours are equal).
 */
export function generateExponentRulePages(settings: ISettingsProps): ProblemData[][] | null {
    const language = printedLanguageOf(settings);
    const perPage = settings.NumberOfProblemsPerPage;
    const signs: ExponentSign[] = ["positive", "zero", "negative"];
    const choices: Cell[] = EXPONENT_RULES.flatMap((rule) => signs.map((sign) => ({ rule, sign })));
    return generateProblemPages<Cell>({
        settings,
        choices,
        dealPage: (count) => dealRuleBySign(count).map(({ row, column }) => ({ rule: row, sign: column })),
        build: ({ rule, sign }, key) => drawExponentRuleProblem(rule, sign, key, language),
        fingerprint: (problem) => `${Math.floor((problem.key - 1) / perPage)}:${problem.display![0]}`,
    });
}
